import { Link } from "react-router-dom";
import { ArrowUpRight, Store, Factory, Globe2 } from "lucide-react";
import { TERMS, PROFILES, TONES, IMAGES } from "@/data/content";
import { Reveal } from "@/components/Reveal";
import { Eyebrow, SectionTitle, GoldButton } from "@/components/Section";
import { Hero } from "@/components/Hero";
import { Outcomes } from "@/components/home/Outcomes";
import { SalesSection } from "@/components/home/SalesSection";
import { ServicesTeaser } from "@/components/home/ServicesTeaser";
import { MethodologyExperience } from "@/components/MethodologyExperience";
import { DiagnosticQuiz } from "@/components/DiagnosticQuiz";
import { InsightsSection } from "@/components/InsightsSection";
import { InsightsTeaser } from "@/components/InsightsTeaser";
import { CtaBanner } from "@/components/CtaBanner";
import { Blob } from "@/components/Decor";

const PROFILE_ICONS = { store: Store, factory: Factory, globe: Globe2 };
const DOT = ["#C26D52", "#2C4A6F", "#5A7265", "#C5A059"];

const Marquee = () => (
  <div className="relative border-y border-[#0E1B31]/[0.07] bg-white/70 backdrop-blur py-4 overflow-hidden" data-testid="terms-marquee">
    <div className="marquee-track gap-4">
      {[...TERMS, ...TERMS].map((t, i) => (
        <span key={i} className="inline-flex items-center gap-2.5 rounded-full border border-[#0E1B31]/[0.08] bg-white px-4 py-2 font-mono-brand text-[11px] tracking-[0.2em] uppercase text-[#3B4A5E] whitespace-nowrap">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: DOT[i % DOT.length] }} />
          {t}
        </span>
      ))}
    </div>
  </div>
);

const AudienceBand = () => (
  <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="audience-band">
    <Blob color="rgba(44,74,111,0.12)" className="-top-40 left-[30%]" size={700} />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow tone="#5A7265">Σε Ποιους Απευθυνόμαστε</Eyebrow>
        <SectionTitle className="mt-6">Τρεις τύποι επιχειρήσεων, <span className="italic text-[#5A7265]">μία κοινή ανάγκη:</span> τάξη και ανάπτυξη.</SectionTitle>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
        {PROFILES.map((p, i) => {
          const Icon = PROFILE_ICONS[p.icon];
          const t = TONES[p.tone];
          return (
            <Reveal key={p.id} delay={i * 0.08} className={i === 1 ? "md:-mt-8" : ""}>
              <Link to="/who-we-serve" data-testid={`audience-card-${p.id}`} className="group relative block h-[460px] overflow-hidden rounded-[32px] surface-hover">
                <img src={IMAGES[p.image]} alt={p.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(14,27,49,0.05) 20%, ${t.accent}F2 100%)` }} />
                <span className="absolute left-6 top-6 flex h-12 w-12 items-center justify-center rounded-2xl glass" style={{ color: t.accent }}>
                  <Icon className="h-5 w-5" />
                </span>
                <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                  <p className="font-mono-brand text-[10px] tracking-[0.3em] uppercase text-white/70">0{i + 1}</p>
                  <h3 className="mt-2 font-display text-2xl font-semibold leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/85">{p.tagline}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold">
                    Δείτε τι βελτιώνουμε <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <Outcomes />
      <SalesSection />
      <ServicesTeaser />
      <AudienceBand />

      {/* METHODOLOGY PREVIEW */}
      <section className="relative py-24 lg:py-32 mesh-slate overflow-hidden" data-testid="methodology-preview">
        <div className="absolute inset-x-0 top-0 hairline" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow tone="#2C4A6F">Πώς Δουλεύουμε</Eyebrow>
            <SectionTitle className="mt-6">Δεν σταματάμε στη <span className="italic text-[#2C4A6F]">συμβουλή.</span></SectionTitle>
            <p className="mt-6 text-base leading-relaxed text-[#3B4A5E]">
              Κάθε συνεργασία ακολουθεί έξι στάδια — και φτάνει μέχρι την εφαρμογή, τη μέτρηση και τη συνεχή βελτίωση.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-14">
            <MethodologyExperience compact />
          </Reveal>
        </div>
      </section>

      {/* AI INSIGHTS */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden" data-testid="ai-section">
        <Blob color="rgba(197,160,89,0.16)" className="-bottom-40 right-[-5%]" size={620} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <InsightsSection />
          </Reveal>
        </div>
      </section>

      <InsightsTeaser />

      {/* QUIZ */}
      <section className="relative py-24 lg:py-32 mesh-copper overflow-hidden" data-testid="quiz-section">
        <div className="absolute inset-x-0 top-0 hairline" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Γρήγορη Διάγνωση</Eyebrow>
            <SectionTitle className="mt-6">Πού θα <span className="italic text-[#C26D52]">ξεκινούσατε;</span></SectionTitle>
            <p className="mt-6 text-base leading-relaxed text-[#3B4A5E]">
              Τρεις γρήγορες ερωτήσεις για να δείτε ποιος άξονας δουλειάς ταιριάζει καλύτερα στη φάση της επιχείρησής σας.
            </p>
            <div className="mt-8">
              <GoldButton to="/who-we-serve" testId="quiz-side-link" variant="ghost">Ή δείτε σε ποιους απευθυνόμαστε</GoldButton>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-8">
            <DiagnosticQuiz />
          </Reveal>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
